import { not } from 'ramda';
import React from 'react';
import { connect } from 'react-redux';
import { compose, withProps } from 'recompose';
import styled from 'styled-components';
import type { UserSelectorState } from '@peloton/auth';
import { parseSearch } from '@peloton/redux';
import type { ResponsiveSelectorState } from '@peloton/responsive';
import { getIsBreakpointLessThan, getIsBreakpointGreaterThan } from '@peloton/responsive';
import { media } from '@peloton/styles';
import { isDefined } from '@peloton/types';
import type { SubsegmentMovement } from '@engage/api-v2';
import type { ClassDetail } from '@engage/class-detail';
import {
  openClassDetail,
  openClassDetailAnalytic,
  closeClassDetail,
  getClassDetail,
  isClassDetailError,
  getIsClassLoading,
  getIsFriendsLoading,
  getFriendsWhoTookClass,
} from '@engage/class-detail';
import type { DenormalizedClass } from '@engage/classes';
import { getDenormalizedClass } from '@engage/classes';
import { gray4, slate1 } from '@engage/colors';
import { useReduxAction, useReduxState } from '@engage/redux';
import { isPending } from '@engage/result';
import { spaces } from '@engage/styles';
import { BookmarkSource, toggleBookmarkClass } from '@members/classes';
import { ModalLabel } from '@members/copy';
import { LoadingSpinner } from '@members/loading';
import type { ModalProps } from '@members/modal';
import { closeModal, StandardModalContainer, LegacyConnectedModal } from '@members/modal';
import { FEED_QUICK_ACTIONS_SOURCE } from '@members/pg-feed/constants';
import { useSearchSharedProperties } from '@members/pg-search';
import { useIsInStacks } from '@members/stacks';

import {
  desktopWidth,
  tabletWidth,
  CLASS_DETAILS_MODAL_NAME,
  CLASS_ID_PARAM,
  SOURCE_PARAM,
} from './constants';
import ModalView from './OnDemandModalView';
import { getClassIdFromLocation } from './selectors';
import ErrorModal from './Views/ErrorModal';

const OnDemandDetailModal: React.FC<
  React.PropsWithChildren<{
    shouldShowButtons: boolean;
    onClose?: () => void;
  }>
> = ({ shouldShowButtons, onClose }) => {
  const classId = useReduxState(getClassIdFromLocation);
  const onModalClose = useReduxAction(closeModal);
  const onFeed = window.location.pathname.includes('feed');
  const queryParams = parseSearch(window.location);
  const sourceDetail = queryParams[SOURCE_PARAM];
  const bookmarkSource = onFeed ? FEED_QUICK_ACTIONS_SOURCE : BookmarkSource.ClassDetails;
  const viewProps: ViewProps = {
    classId,
    classIsLoading: useReduxState(getIsClassLoading),
    friendsIsLoading: useReduxState(getIsFriendsLoading),
    fetchDetailsDidError: useReduxState(isClassDetailError),
    friendsWhoHaveTakenClass: useReduxState(getFriendsWhoTookClass),
    klass: useReduxState(getDenormalizedClass, classId) as DenormalizedClass,
    detail: useReduxState(getClassDetail),
    openClassDetail: useReduxAction(openClassDetail),
    openClassDetailAnalytic: useReduxAction(openClassDetailAnalytic),
    closeClassDetail: useReduxAction(closeClassDetail),
    onReloadClick: useReduxAction(openClassDetail),
    onBookmarkClick: useReduxAction(toggleBookmarkClass(bookmarkSource, sourceDetail)),
    closeModal: () => onModalClose([CLASS_ID_PARAM]),
  };
  const joinToken = viewProps.klass?.joinTokens?.onDemand ?? '';
  const isInStack = useIsInStacks(joinToken);

  const searchSharedProperties = useSearchSharedProperties();

  React.useEffect(() => {
    if (!isPending(isInStack)) {
      viewProps.openClassDetailAnalytic(
        viewProps.classId,
        isInStack,
        searchSharedProperties,
      );
    }
  }, [isInStack.type, searchSharedProperties]);

  React.useEffect(() => {
    viewProps.openClassDetail(viewProps.classId);
    return () => {
      viewProps.closeClassDetail();
      onClose && onClose();
    };
  }, []);

  if (
    isDefined(viewProps.klass) &&
    !viewProps.classIsLoading &&
    viewProps.fetchDetailsDidError
  ) {
    return <ErrorModal {...viewProps} />;
  } else if (
    viewProps.classIsLoading ||
    viewProps.friendsIsLoading ||
    isPending(isInStack)
  ) {
    return <Loading />;
  } else if (not(isDefined(viewProps.klass))) {
    return <NoClassDetailsLoading />;
  }
  return <ModalView shouldShowButtons={shouldShowButtons} {...viewProps} />;
};

const tabletStyleOverrides: StyleOverrideProps['style'] = {
  content: {
    maxWidth: `${tabletWidth}px`,
    backgroundColor: 'transparent',
  },
};

const desktopStyleOverrides: StyleOverrideProps['style'] = {
  content: {
    maxWidth: `${desktopWidth}px`,
    backgroundColor: 'transparent',
  },
};

const toOverriddenStyles = (
  isMobile: boolean,
  isDesktop: boolean,
  overrideTabletStyleWithDesktop: boolean,
): StyleOverrideProps['style'] => {
  if (isMobile) {
    return {};
  } else if (isDesktop || overrideTabletStyleWithDesktop) {
    return desktopStyleOverrides;
  }

  return tabletStyleOverrides;
};

export const ConnectedOnDemandModal = compose<
  StyleOverrideProps & {
    shouldUseStyleOverrides: boolean;
    shouldShowButtons: boolean;
    onClose?: () => void;
  },
  {
    overrideTabletStyleWithDesktop?: boolean;
    shouldShowButtons?: boolean;
    onClose?: () => void;
  }
>(
  connect<{ isMobile: boolean; isDesktop: boolean }>(
    (state: ResponsiveSelectorState & UserSelectorState) => ({
      isMobile: getIsBreakpointLessThan(state, 'tablet'),
      isDesktop: getIsBreakpointGreaterThan(state, 'tabletXLarge'),
    }),
  ),
  withProps<
    StyleOverrideProps,
    {
      isMobile: boolean;
      isDesktop: boolean;
      overrideTabletStyleWithDesktop?: boolean;
      onClose?: () => void;
    }
  >(({ isMobile, isDesktop, overrideTabletStyleWithDesktop = false }) => ({
    style: toOverriddenStyles(isMobile, isDesktop, overrideTabletStyleWithDesktop),
    hideCloseButton: isMobile,
  })),
)(({ style, hideCloseButton, shouldShowButtons = true, onClose }) => (
  <LegacyConnectedModal
    modalNames={{
      [CLASS_DETAILS_MODAL_NAME]: {
        component: (
          <OnDemandDetailModal shouldShowButtons={shouldShowButtons} onClose={onClose} />
        ),
        modelIdentifiers: [CLASS_ID_PARAM],
        style,
        ariaLabelId: ModalLabel.ClassDetails,
        hideCloseButton: hideCloseButton,
      },
    }}
  />
));

export type ViewProps = {
  klass: DenormalizedClass;
  detail: ClassDetail | undefined;
  friendsWhoHaveTakenClass: string[];
  classId: string;
  classIsLoading: boolean;
  friendsIsLoading: boolean;
  fetchDetailsDidError: boolean;
  openClassDetail: typeof openClassDetail;
  openClassDetailAnalytic: typeof openClassDetailAnalytic;
  closeClassDetail: typeof closeClassDetail;
  onBookmarkClick: (t1: string, t2: boolean) => ReturnType<typeof toggleBookmarkClass>;
  onReloadClick: typeof openClassDetail;
  closeModal: () => void;
  scheduleId?: string;
  setScheduleId?: React.Dispatch<React.SetStateAction<string>>;
  setCurrentVideoSelected?: React.Dispatch<React.SetStateAction<SubsegmentMovement>>;
  setCurrentMovementGroup?: React.Dispatch<
    React.SetStateAction<
      { name: string | undefined; id: string; selectedSegmentId: string } | undefined
    >
  >;
  currentVideoSelected?: SubsegmentMovement;
  currentMovementGroup?: {
    name: string | undefined;
    id: string;
    selectedSegmentId: string;
  };
};

const NoClassDetailsLoading = () => (
  <Container>
    <Spinner isLoading size={40} />
  </Container>
);

const Container = styled(StandardModalContainer)`
  padding: 30px 10px;
  ${media.tablet`
    padding: ${spaces.huge}px 30px;
  `}
`;

const LoadingSpinnerContainer = styled.div`
  height: 50vh;
`;

const Loading = () => (
  <LoadingSpinnerContainer>
    <LoadingSpin isLoading size={40} />
  </LoadingSpinnerContainer>
);

const Spinner = styled(LoadingSpinner)`
  fill: ${gray4};
`;

const LoadingSpin = styled(LoadingSpinner)`
  display: block;
  fill: ${slate1};
  margin: ${spaces.giant}px auto 0;
  ${media.tablet`
    fill: ${gray4};
  `}
`;

type StyleOverrideProps = Pick<ModalProps, 'style' | 'hideCloseButton'>;
